import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";
import SoftButton from "components/SoftButton";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { formatCredentialApplicationType } from "helpers/credentials";
import defaultCredentialIcon from "assets/images/credential.svg";
import { useSlugCredential } from "hooks/useSlugCredential";
import { useResourceListener } from "hooks/useResourceListener";
import { CredentialTabs } from "./components/tabs";
import CredentialDialogForm from "layouts/pages/credential-management/components/CredentialDialogForm";
import CredentialDeleteDialog from "layouts/pages/credential-management/components/CredentialDeleteDialog";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import { openCredentialDialog, setCredentialDialogMode, setSelectedCredential } from "redux/actions/portal/credentialManagementActionCreator";
import { useCredentialPermissions } from "hooks/useCredentialPermissions";
import "./style.css";

export const LibraryCredential = () => {
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { credential, handleFetchCredentials } = useSlugCredential();
   const { users } = useSelector((store) => store.portal.users);
   const { canEdit, canDelete } = useCredentialPermissions(credential);
   useResourceListener("credentialsSharingUpdate", handleFetchCredentials);

   if (!credential) return <></>;

   const ownerDeveloper = users?.find((user) => user.auth0_id === credential?.owner_id);

   const openCredentialDialogByMode = (mode) => {
      dispatch(setCredentialDialogMode(mode));
      dispatch(setSelectedCredential(credential));
      dispatch(openCredentialDialog());
   };

   return (
      <>
         <div style={{ padding: "8px", position: "sticky", top: 0, zIndex: 1000, backgroundColor: "var(--main-bg-color)" }}>
            <DashboardNavbar title={credential?.application?.name ?? ""} />
         </div>
         <DashboardLayout sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", height: "100vh", boxSizing: "border-box", overflow: "hidden" }}>
            <SoftBox mt={-3} sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
               <Card className="credential" sx={{ backgroundColor: "transparent !important", overflow: "hidden", boxShadow: "none !important", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                  <Card sx={{ overflow: "visible", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                     <SoftBox
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{
                           display: { sm: "block", lg: "flex" },
                           padding: { sm: "12px 32px 20px 32px", lg: "48px 64px 46px 64px" },
                           flexWrap: { sm: "wrap", lg: "noWrap" },
                           columnGap: "40px",
                           height: "max-content"
                        }}
                     >
                        <SoftBox display="flex" alignItems="flex-start" sx={{ marginBottom: { sm: "16px", lg: "0" }, position: "relative" }}>
                           <SoftBox sx={{ position: "absolute", top: "-32px", left: 0 }}>
                              <button onClick={() => navigate("/administration/credential-management")} className="credential-go-back">
                                 <SoftBox sx={{ display: "grid", marginRight: "16px", placeContent: "center", fontSize: "28px", fontWeight: "300" }}>
                                    <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                       <path d="M8.77783 0.5L0.999657 8.27817L8.77783 16.0563" stroke="#7B809A" />
                                    </svg>
                                 </SoftBox>
                                 <SoftTypography sx={{ fontWeight: "normal", fontSize: "14px" }}>Go back</SoftTypography>
                              </button>
                           </SoftBox>
                           <SoftBox
                              component="img"
                              marginRight="40px"
                              sx={{
                                 objectFit: "cover",
                                 borderRadius: credential?.logo ? "16px" : { sm: "16px", lg: "23px" },
                                 width: {
                                    sm: "120px",
                                    lg: "174px"
                                 },
                                 height: {
                                    sm: credential?.logo ? "120px" : "116px",
                                    lg: credential?.logo ? "164px" : "168px"
                                 },
                                 minWidth: {
                                    sm: "120px",
                                    lg: "174px"
                                 },
                                 maxWidth: {
                                    sm: "120px",
                                    lg: "174px"
                                 },
                                 minHeight: {
                                    sm: credential?.logo ? "120px" : "116px",
                                    lg: credential?.logo ? "164px" : "168px"
                                 },
                                 maxHeight: {
                                    sm: credential?.logo ? "120px" : "116px",
                                    lg: credential?.logo ? "164px" : "168px"
                                 },
                                 border: credential?.logo ? "1px solid var(--portal-surface-border)" : "none",
                                 boxSizing: "border-box"
                              }}
                              src={credential?.logo ? `${credential.logo}?v=${credential.updated_date_time ?? ""}` : defaultCredentialIcon}
                           />
                           <SoftBox sx={{ maxWidth: { lg: "clamp(100px, 32vw, 540px)" }, display: "flex", flexDirection: "column", minHeight: "164px" }}>
                              <SoftTypography fontSize="24px" fontWeight="bold" lineHeight="1.25" marginBottom="8px" className="white-to-dark">
                                 {credential.application?.name}
                              </SoftTypography>
                              <StatusBadge status="active" />
                              <SoftTypography sx={{ margin: "auto 0", fontSize: "13px" }}>{credential.application?.description || formatCredentialApplicationType(credential.application?.applicationType) || "No description"}</SoftTypography>
                              <SoftBox display="flex" sx={{ marginTop: "auto", columnGap: "20px", flexWrap: "wrap", rowGap: "12px" }}>
                                 <SoftButton sx={{ padding: "12px !important" }} variant="gradient" size="medium" color="primary" onClick={() => openCredentialDialogByMode("view")}>
                                    <svg style={{ marginRight: "8px" }} width="12" height="12" viewBox="0 1 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                       <g clipPath="url(#credential-open-icon)">
                                          <path d="M4.12516 0.914062V3.66406H5.04183V5.03906H2.29183C1.78308 5.03906 1.37516 5.44698 1.37516 5.95573V7.33073H0.458496V10.0807H3.2085V7.33073H2.29183V5.95573H5.04183V7.33073H4.12516V10.0807H6.87516V7.33073H5.9585V5.95573H8.7085V7.33073H7.79183V10.0807H10.5418V7.33073H9.62516V5.95573C9.62516 5.44698 9.21725 5.03906 8.7085 5.03906H5.9585V3.66406H6.87516V0.914062H4.12516Z" fill="white" />
                                       </g>
                                       <defs>
                                          <clipPath id="credential-open-icon">
                                             <rect width="11" height="11" fill="white" transform="translate(0 -0.00292969)" />
                                          </clipPath>
                                       </defs>
                                    </svg>
                                    open
                                 </SoftButton>
                                 <SoftButton sx={{ padding: "12px !important" }} variant="gradient" size="medium" color="primary" onClick={() => openCredentialDialogByMode(canEdit ? "edit" : "view")} disabled={!canEdit && !canDelete}>
                                    <Icon sx={{ fontSize: "500px !important", marginRight: "8px" }}>edit</Icon>
                                    properties
                                 </SoftButton>
                              </SoftBox>
                           </SoftBox>
                        </SoftBox>
                        <SoftBox sx={{ flexBasis: { lg: "max-content" }, flexShrink: 0 }}>
                           <SoftTypography className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Version:</span>
                              <span style={{ marginLeft: "10px" }}>02.03.22</span>
                           </SoftTypography>
                           <SoftTypography sx={{ display: "flex", alignItems: "flexStart" }} className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Date Created:</span>
                              <span style={{ marginLeft: "10px", maxWidth: "100px", whiteSpace: "break-spaces" }}>{formatDateAndTime(getLocalDateTimeString(credential.create_date_time)) || ""}</span>
                           </SoftTypography>
                           {credential.updated_date_time && (
                              <SoftTypography sx={{ display: "flex", alignItems: "flexStart" }} className="white-to-dark" fontSize="12px" marginBottom="4px">
                                 <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Last Edited:</span>
                                 <span style={{ marginLeft: "10px", maxWidth: "100px", whiteSpace: "break-spaces" }}>{formatDateAndTime(getLocalDateTimeString(credential.updated_date_time))}</span>
                              </SoftTypography>
                           )}
                           <SoftBox display="flex" alignItems="center">
                              <SoftTypography className="white-to-dark" opacity={0.6} fontSize="12px">
                                 Developed By:
                              </SoftTypography>
                              {ownerDeveloper && (
                                 <>
                                    {ownerDeveloper.image && ownerDeveloper.image.trim() !== "" ? (
                                       <SoftBox
                                          component="img"
                                          src={ownerDeveloper.image}
                                          alt={`${ownerDeveloper.first_name} ${ownerDeveloper.last_name}`}
                                          sx={{
                                             width: "24px",
                                             height: "24px",
                                             borderRadius: "32px",
                                             border: ".5px solid white !important",
                                             objectFit: "cover",
                                             margin: "0 8px"
                                          }}
                                       />
                                    ) : (
                                       <DefaultUserAvatar firstName={ownerDeveloper.first_name} lastName={ownerDeveloper.last_name} variant={"rounded"} size="xs" border=".5px solid white !important" disableFocusListener={false} disableTouchListener={false} open={false} />
                                    )}
                                    <SoftTypography component="span" className="white-to-dark" fontSize="12px" sx={{ textDecoration: "underline", marginLeft: ownerDeveloper.image ? 0 : "8px" }}>
                                       {ownerDeveloper.first_name} {ownerDeveloper.last_name}
                                    </SoftTypography>
                                 </>
                              )}
                           </SoftBox>
                        </SoftBox>
                     </SoftBox>
                     <SoftBox sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <CredentialTabs />
                     </SoftBox>
                  </Card>
               </Card>
            </SoftBox>
            <CredentialDialogForm />
            <CredentialDeleteDialog onDeleted={() => navigate("/administration/credential-management")} />
         </DashboardLayout>
      </>
   );
};
